import React, { useEffect, useState } from 'react';
import { safeGet } from '../../api/safeFetch';
import type { Locale } from '../../i18n';

type Session = 'premarket' | 'open' | 'postmarket';
interface SessionPoint {
  price: number | null;
  ts_ms: number | null;
  state: 'no_data' | 'historical' | 'fresh' | 'stale';
  change_pct: number | null;
}
export interface ExtendedSnapshot {
  ok: true;
  read_only: true;
  symbol: string;
  source: 'tradier_production_timesales';
  interval: '1min';
  data_date: string;
  market_state: string;
  as_of_ms: number;
  regular_close: number | null;
  sessions: Record<Session, SessionPoint>;
  bars: { ts_ms: number; close: number; session: Session }[];
}

const SESSIONS: Session[] = ['premarket', 'open', 'postmarket'];
const LABELS = {
  zh: { premarket: '盘前', open: '常规时段', postmarket: '盘后', closed: '休市' },
  en: { premarket: 'Pre-market', open: 'Regular session', postmarket: 'After-hours', closed: 'Closed' },
};
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const nullableNumber = (value: unknown) => value === null || finite(value);

export function isExtendedSnapshot(raw: unknown, symbol: string): raw is ExtendedSnapshot {
  if (!raw || typeof raw !== 'object') return false;
  const value = raw as ExtendedSnapshot;
  return value.ok === true && value.read_only === true && value.symbol === symbol
    && value.source === 'tradier_production_timesales' && value.interval === '1min'
    && /^\d{4}-\d{2}-\d{2}$/.test(value.data_date) && finite(value.as_of_ms)
    && ['premarket', 'open', 'postmarket', 'closed'].includes(value.market_state)
    && nullableNumber(value.regular_close)
    && SESSIONS.every(session => {
      const point = value.sessions?.[session];
      return point && nullableNumber(point.price) && nullableNumber(point.ts_ms)
        && nullableNumber(point.change_pct) && ['fresh', 'stale', 'historical', 'no_data'].includes(point.state);
    }) && Array.isArray(value.bars) && value.bars.every(bar =>
      finite(bar.ts_ms) && finite(bar.close) && SESSIONS.includes(bar.session));
}

function et(stamp: number | null, locale: Locale) {
  if (!finite(stamp)) return '—';
  return new Intl.DateTimeFormat(locale === 'zh' ? 'zh-CN' : 'en-US', {
    timeZone: 'America/New_York', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(stamp) + ' ET';
}

export function ExtendedHoursView({ data, locale, failed = false }: {
  data: ExtendedSnapshot; locale: Locale; failed?: boolean;
}) {
  const zh = locale === 'zh';
  const states = zh
    ? { fresh: '近期分时', stale: '数据较旧', historical: '历史数据', no_data: '暂无成交分时' }
    : { fresh: 'Recent minute', stale: 'Stale', historical: 'Historical', no_data: 'No traded minutes' };
  return <div className="space-y-3">
    <p className="text-xs text-gray-400">
      {data.symbol} · {data.data_date} · {LABELS[locale][data.market_state as keyof typeof LABELS.en]}
      {' · '}{zh ? '常规收盘价' : 'Regular close'} ${data.regular_close?.toFixed(2) ?? '—'}
      {' · '}{zh ? '检查于' : 'Checked'} {et(data.as_of_ms, locale)}
    </p>
    <div className="grid gap-2 sm:grid-cols-3">
      {SESSIONS.map(session => {
        const point = data.sessions[session];
        return <div key={session} className="rounded border border-gray-700 bg-gray-950/50 p-3">
          <div className="flex justify-between gap-2 text-xs text-gray-400">
            <span>{LABELS[locale][session]}</span>
            <span className={failed || point.state === 'stale' ? 'text-amber-400' : ''}>
              {states[failed && point.price !== null ? 'stale' : point.state]}
            </span>
          </div>
          <div className="mt-1 font-mono text-lg text-gray-100">{point.price === null ? '—' : `$${point.price.toFixed(2)}`}</div>
          <div className="text-[11px] text-gray-500">{et(point.ts_ms, locale)} · {zh ? '分钟起点' : 'Minute start'}</div>
          {session === 'postmarket' && <div className="mt-1 text-xs text-gray-400">
            {zh ? '较常规收盘' : 'vs regular close'}: {point.change_pct === null ? '—' : `${point.change_pct > 0 ? '+' : ''}${point.change_pct.toFixed(2)}%`}
          </div>}
        </div>;
      })}
    </div>
    <details className="text-xs text-gray-400">
      <summary className="cursor-pointer">{zh ? '查看最近盘前／盘后分时' : 'Recent extended-session minutes'}</summary>
      <div className="mt-2 max-h-48 overflow-auto">
        <table className="w-full text-left font-mono">
          <thead><tr><th>{zh ? '时间 ET' : 'Time ET'}</th><th>{zh ? '时段' : 'Session'}</th><th>{zh ? '分钟收价' : 'Minute close'}</th></tr></thead>
          <tbody>{data.bars.filter(bar => bar.session !== 'open').slice(-30).reverse().map(bar =>
            <tr key={bar.ts_ms}><td>{et(bar.ts_ms, locale)}</td><td>{LABELS[locale][bar.session]}</td><td>${bar.close.toFixed(4)}</td></tr>)}</tbody>
        </table>
      </div>
    </details>
  </div>;
}

export function ExtendedHoursPanel({ locale }: { locale: Locale }) {
  const zh = locale === 'zh';
  const [symbol, setSymbol] = useState('AAPL');
  const [draft, setDraft] = useState('AAPL');
  const [data, setData] = useState<ExtendedSnapshot | null>(null);
  const [error, setError] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let controller: AbortController | undefined;
    const load = async () => {
      if (document.visibilityState === 'hidden') {
        timer = setTimeout(load, 30_000);
        return;
      }
      controller = new AbortController();
      const timeout = setTimeout(() => controller?.abort(), 25_000);
      setBusy(true);
      try {
        const next = await safeGet<unknown>(`/api/broker/extended-hours?symbol=${encodeURIComponent(symbol)}`, { signal: controller.signal });
        if (!isExtendedSnapshot(next, symbol)) throw new Error('Invalid market data');
        if (!stopped) { setData(next); setError(false); }
      } catch {
        if (!stopped) setError(true);
      } finally {
        clearTimeout(timeout);
        if (!stopped) { setBusy(false); timer = setTimeout(load, 30_000); }
      }
    };
    setData(null);
    setError(false);
    void load();
    return () => { stopped = true; clearTimeout(timer); controller?.abort(); };
  }, [symbol]);

  return <section className="space-y-3 rounded-xl border border-gray-800 bg-gray-900 p-3 sm:p-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="text-sm font-semibold text-gray-200">{zh ? 'Tradier 盘前／盘后行情' : 'Tradier extended-hours prices'}</h2>
      <form className="flex items-center gap-2" onSubmit={event => {
        event.preventDefault();
        const next = draft.trim().toUpperCase();
        if (!/^[A-Z]{1,6}(?:[./-][A-Z]{1,2})?$/.test(next)) { setInvalid(true); return; }
        setInvalid(false); setSymbol(next); setDraft(next);
      }}>
        <label className="sr-only" htmlFor="extended-symbol">{zh ? '股票或 ETF 代码' : 'Stock or ETF symbol'}</label>
        <input id="extended-symbol" value={draft} onChange={event => setDraft(event.target.value)} maxLength={10}
          className="w-28 rounded border border-gray-600 bg-gray-950 px-2 py-1 text-xs uppercase text-gray-100" />
        <button type="submit" className="rounded border border-gray-600 px-3 py-1 text-xs text-gray-200">{zh ? '查询' : 'Load'}</button>
        {busy && <span className="text-xs text-gray-500">{zh ? '读取中…' : 'Loading…'}</span>}
      </form>
    </div>
    <p className="text-[11px] text-gray-500">{zh
      ? '只读 · 股票 / ETF · Tradier 正式行情源 · 已完成的 1 分钟分时，约每 30 秒检查 · 不参与评分或自动下单'
      : 'Read-only · Stocks / ETFs · Tradier production data · Completed 1-minute bars, checked about every 30s · Not used for scoring or orders'}</p>
    {invalid && <p role="alert" className="text-xs text-amber-400">{zh ? '请输入有效股票或 ETF 代码，不支持期权合约。' : 'Enter a valid stock or ETF symbol, not an option contract.'}</p>}
    {error && <p role="alert" className="text-xs text-amber-400">{zh
      ? '行情暂不可用：请检查代码、正式行情权限或稍后重试；保留数据不代表实时。'
      : 'Data unavailable: check symbol, production entitlement, or retry later; retained data is not live.'}</p>}
    {data?.symbol === symbol && <ExtendedHoursView data={data} locale={locale} failed={error} />}
    {!data && !error && <p className="text-xs text-gray-500">{zh ? '等待行情，不以常规收盘价代替盘后价格。' : 'Waiting for data; regular close is not an after-hours price.'}</p>}
  </section>;
}
